import { common, createLowlight } from 'lowlight';

const highlighter = createLowlight(common);
type HighlightNode = ReturnType<typeof highlighter.highlight>['children'][number];

export interface CodeToken {
  text: string;
  className?: string;
}

const languageAliases: Record<string, string> = {
  js: 'javascript',
  mjs: 'javascript',
  cjs: 'javascript',
  jsx: 'javascript',
  ts: 'typescript',
  tsx: 'typescript',
  py: 'python',
  sh: 'bash',
  zsh: 'bash',
  shell: 'shell',
  console: 'shell',
  yml: 'yaml',
  md: 'markdown',
  html: 'xml',
  htm: 'xml',
  svg: 'xml',
  txt: 'plaintext',
  text: 'plaintext',
  toml: 'ini',
  cfg: 'ini',
  h: 'c',
  patch: 'diff',
};

/** Maps fence names, aliases and file extensions to a registered grammar, else plain text. */
export function normalizeLanguage(language: string | undefined): string {
  const key = (language ?? '').trim().toLowerCase();
  const name = languageAliases[key] ?? key;
  return name && highlighter.registered(name) ? name : 'plaintext';
}

const plainFiles = new Set(['dockerfile', 'license', 'makefile']);

/** Grammar for a file path such as `src/app.py` or `tests/test_core.py`. */
export function languageForPath(path: string): string {
  const base = path.split('/').pop() ?? path;
  const lower = base.toLowerCase();
  if (lower === 'makefile') return 'makefile';
  if (plainFiles.has(lower) || lower.endsWith('.lock')) return 'plaintext';
  const dot = lower.lastIndexOf('.');
  if (dot < 0) return 'plaintext';
  return normalizeLanguage(lower.slice(dot + 1));
}

const shellSeparators = new Set(['&&', '||', '|', ';']);

/**
 * highlight.js leaves `python3 lab.py doctor` untouched because commands are
 * not keywords. Mark the command word and its flags so command blocks read
 * like code instead of plain prose; strings and comments keep their own classes.
 */
function decorateShell(lines: CodeToken[][]): CodeToken[][] {
  return lines.map((line) => {
    let commandSeen = false;
    const decorated: CodeToken[] = [];
    for (const token of line) {
      if (token.className) {
        decorated.push(token);
        continue;
      }
      for (const part of token.text.split(/(\s+)/)) {
        if (!part) continue;
        if (/^\s+$/.test(part)) {
          decorated.push({ text: part });
        } else if (shellSeparators.has(part)) {
          decorated.push({ text: part, className: 'hljs-keyword' });
          commandSeen = false;
        } else if (!commandSeen) {
          if (part === '$' || part === 'sudo') decorated.push({ text: part });
          else {
            decorated.push({ text: part, className: 'hljs-built_in' });
            commandSeen = true;
          }
        } else if (/^--?[a-z]/i.test(part)) {
          decorated.push({ text: part, className: 'hljs-attr' });
        } else {
          decorated.push({ text: part });
        }
      }
    }
    return decorated;
  });
}

/** Keep the highlighter's text and styling only; never render generated HTML. */
export function highlightCodeLines(code: string, language: string): CodeToken[][] {
  const plain = () => code.split('\n').map((text) => [{ text }]);
  const grammar = normalizeLanguage(language);
  // Large or unfamiliar examples remain readable, without language autodetection on each delta.
  if (grammar === 'plaintext' || code.length > 60_000) return plain();
  try {
    let current: CodeToken[] = [];
    const lines: CodeToken[][] = [current];
    function visit(node: HighlightNode, classes: string[] = []) {
      if (node.type === 'text') {
        node.value.split('\n').forEach((text, index) => {
          if (index) {
            current = [];
            lines.push(current);
          }
          if (text) current.push({ text, className: classes.join(' ') });
        });
      } else if (node.type === 'element') {
        const own = node.properties.className;
        const nested = Array.isArray(own) ? [...classes, ...own.map(String)] : classes;
        for (const child of node.children) visit(child, nested);
      }
    }
    for (const node of highlighter.highlight(grammar, code).children) visit(node);
    return grammar === 'bash' || grammar === 'shell' ? decorateShell(lines) : lines;
  } catch {
    return plain();
  }
}
